/**
 * Chrome visible en perfil portal (patient).
 * Defaults de prod; overrides solo en developer (localStorage).
 */

import { STORAGE_KEYS, UDV_PLANS } from './constants'
import { isDeveloperMode } from './resolveAccess'
import { notifyPlanChange } from './storage'

/** @typedef {'sistema'|'nav'|'pet'|'gsps'|'mpr'|'imagen'|'estudio'|'medir'|'roi'|'render'|'qa'|'gatewayBadge'} PortalChromeSectionId */

/** @type {{ id: PortalChromeSectionId, label: string }[]} */
export const PORTAL_CHROME_SECTION_META = [
  { id: 'sistema', label: 'Sistema (Orthanc, Gateway, avisos…)' },
  { id: 'nav', label: 'Nav / Navegación' },
  { id: 'pet', label: 'PET' },
  { id: 'gsps', label: 'GSPS' },
  { id: 'mpr', label: 'MPR (sigue sujeto a feature patient)' },
  { id: 'imagen', label: 'Imagen (cine, WW/WC, zoom, pan…)' },
  { id: 'estudio', label: 'Estudio (header, dosis)' },
  { id: 'medir', label: 'Medir' },
  { id: 'roi', label: 'ROI' },
  { id: 'render', label: 'Render' },
  { id: 'qa', label: 'QA / QA Screen' },
  { id: 'gatewayBadge', label: 'Badge Gateway (footer Studio)' },
]

/**
 * Defaults portal: Sistema (Gateway desactivado en UI) + Nav + Imagen.
 * Herramientas clínicas (MPR, medir, ROI…) ocultas. Login/nube solo con invite.
 * @type {Record<PortalChromeSectionId, boolean>}
 */
export const PORTAL_CHROME_SECTION_DEFAULTS = {
  sistema: true,
  nav: true,
  pet: false,
  gsps: false,
  mpr: false,
  imagen: true,
  estudio: false,
  medir: false,
  roi: false,
  render: false,
  qa: false,
  gatewayBadge: false,
}

/**
 * @typedef {{
 *   hideToolsRail: boolean,
 *   sections: Record<string, boolean>,
 * }} PortalChromeConfig
 */

export const PORTAL_CHROME_DEFAULT_CONFIG = {
  /** Desktop Studio: ocultar toda la barra derecha de tools. */
  hideToolsRail: false,
  sections: { ...PORTAL_CHROME_SECTION_DEFAULTS },
}

function canUseStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage
  } catch (e) {
    return false
  }
}

/**
 * Overrides de lab (solo developer).
 * @returns {Partial<PortalChromeConfig>|null}
 */
export function getPortalChromeOverrides() {
  if (!canUseStorage()) return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.PORTAL_CHROME)
    if (!raw) return null
    const v = JSON.parse(raw)
    if (!v || typeof v !== 'object') return null
    return v
  } catch (e) {
    return null
  }
}

/**
 * @param {Partial<PortalChromeConfig>|null} cfg
 */
export function setPortalChromeOverrides(cfg) {
  if (!canUseStorage()) return
  try {
    if (cfg == null) {
      window.localStorage.removeItem(STORAGE_KEYS.PORTAL_CHROME)
    } else {
      window.localStorage.setItem(STORAGE_KEYS.PORTAL_CHROME, JSON.stringify(cfg))
    }
  } catch (e) {
    /* ignore */
  }
  notifyPlanChange()
}

/**
 * Config efectiva para portal. En production ignora overrides de lab.
 * @returns {PortalChromeConfig}
 */
export function getPortalChromeConfig() {
  const base = {
    hideToolsRail: PORTAL_CHROME_DEFAULT_CONFIG.hideToolsRail,
    sections: { ...PORTAL_CHROME_SECTION_DEFAULTS },
  }
  if (!isDeveloperMode()) return base
  const ov = getPortalChromeOverrides()
  if (!ov) return base
  if (typeof ov.hideToolsRail === 'boolean') {
    base.hideToolsRail = ov.hideToolsRail
  }
  if (ov.sections && typeof ov.sections === 'object') {
    Object.keys(PORTAL_CHROME_SECTION_DEFAULTS).forEach((id) => {
      if (typeof ov.sections[id] === 'boolean') {
        base.sections[id] = ov.sections[id]
      }
    })
  }
  return base
}

/**
 * @param {string|null|undefined} plan
 * @returns {PortalChromeConfig|null} null = no filtrar (plan no portal)
 */
export function getPortalChromeForPlan(plan) {
  if (plan !== UDV_PLANS.PATIENT) return null
  return getPortalChromeConfig()
}

/**
 * @param {PortalChromeConfig|null|undefined} chrome
 * @param {PortalChromeSectionId|string} sectionId
 * @returns {boolean}
 */
export function portalSectionVisible(chrome, sectionId) {
  if (!chrome || !chrome.sections) return true
  if (chrome.sections[sectionId] === false) return false
  return chrome.sections[sectionId] !== false
}
